import {
  ActivationRequiredAlert,
  EmailNotFoundAlert,
  ForgotPasswordSuccessAlert,
  UnexpectedErrorAlert,
} from '@/components/alerts';
import { EMAIL_REGEX, EMAIL_VALIDATION_MESSAGE } from '@/constants';
import { CustomInput } from '@/components/CustomInput';
import { ChevronLeftIcon } from '@/components/icons';
import { SubmitButton } from '@/components/SubmitButton';
import { TurnstileField } from '@/components/TurnstileField';
import { AccountCenterBox } from '@/layout/AccountCenterBox';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useTurnstile } from '@/hooks/useTurnstile';
import { api } from '@/services/api';
import { ForgotPasswordResponse, ForgotPasswordResultEnum } from '@shared/types';
import { AxiosResponse } from 'axios';
import { Field, Form, Formik } from 'formik';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';

enum FormStatus {
  NoAlert = 'NoAlert',
  ForgotPasswordSuccessAlert = 'ForgotPasswordSuccessAlert',
  ActivationRequiredAlert = 'ActivationRequiredAlert',
  EmailNotFoundAlert = 'EmailNotFoundAlert',
  UnexpectedError = 'UnexpectedErrorAlert',
}

export const ForgotPassword = () => {
  useDocumentTitle('Marshall Reddick - Forgot Password');
  const { getToken, reset, widgetProps, widgetError } = useTurnstile();
  const alertMap: Partial<Record<FormStatus, React.ReactNode>> = {
    [FormStatus.ForgotPasswordSuccessAlert]: <ForgotPasswordSuccessAlert />,
    [FormStatus.ActivationRequiredAlert]: <ActivationRequiredAlert />,
    [FormStatus.EmailNotFoundAlert]: <EmailNotFoundAlert />,
    [FormStatus.UnexpectedError]: <UnexpectedErrorAlert />,
  };
  return (
    <Formik
      initialValues={{ email: '', turnstileToken: '' }}
      initialStatus={FormStatus.NoAlert}
      validationSchema={Yup.object().shape({
        email: Yup.string()
          .trim()
          .matches(EMAIL_REGEX, EMAIL_VALIDATION_MESSAGE)
          .transform((value) => value.trim()),
      })}
      validateOnMount={false}
      onSubmit={async (values, { setErrors, setStatus, setFieldError }) => {
        if (!values.email.trim()) {
          setErrors({ email: 'Email is required' });
          return;
        }

        const turnstileToken = await getToken();

        if (!turnstileToken) {
          setFieldError('turnstileToken', 'Security check failed, please try again');
          reset();
          return;
        }

        setStatus(FormStatus.NoAlert);

        try {
          const response: AxiosResponse<ForgotPasswordResponse> = await api.post(
            '/auth/forgot-password',
            {
              email: values.email,
              turnstileToken,
            },
          );

          const { result } = response.data;
          if (result === ForgotPasswordResultEnum.SUCCESS) {
            setStatus(FormStatus.ForgotPasswordSuccessAlert);
          } else if (result === ForgotPasswordResultEnum.USER_NOT_FOUND) {
            setStatus(FormStatus.EmailNotFoundAlert);
          } else if (result === ForgotPasswordResultEnum.UNVERIFIED) {
            setStatus(FormStatus.ActivationRequiredAlert);
          } else {
            setStatus(FormStatus.UnexpectedError);
          }
        } catch {
          setStatus(FormStatus.UnexpectedError);
        } finally {
          reset();
        }
      }}
    >
      {({ errors, touched, isSubmitting, status }) => {
        const statusKey = status as FormStatus;
        return (
          <AccountCenterBox
            title="Forgot your password?"
            description="Enter the email that you used to register. You will be sent an email with
                instructions to reset your password."
            isSubmitting={isSubmitting}
          >
            <Form noValidate>
              {statusKey in alertMap ? <>{alertMap[statusKey]}</> : null}
              {statusKey !== FormStatus.ForgotPasswordSuccessAlert && (
                <>
                  <Field
                    name="email"
                    as={CustomInput}
                    type="email"
                    label="Email Address"
                    error={errors.email}
                    touched={touched.email}
                  />
                  <TurnstileField
                    widgetProps={widgetProps}
                    widgetError={widgetError}
                    error={errors.turnstileToken}
                  />
                  <SubmitButton
                    text="Send Email"
                    className="mt-2"
                    isSubmitting={isSubmitting}
                    disabled={false}
                  />
                </>
              )}
              <div className="flex justify-center text-sm pt-1 mt-3">
                <Link to="/login" className="text-link">
                  <ChevronLeftIcon /> Back to Log In
                </Link>
              </div>
            </Form>
          </AccountCenterBox>
        );
      }}
    </Formik>
  );
};
